<script setup>
/**
 * The whole menu, as five sections in the order an owner would explain it: what it is and when
 * it shows → its options → the words around it → what the customer will see → whether it runs.
 * `TaskForm`'s anatomy line for line (SettingsHeader per section, `h-px bg-n-weak` rules,
 * `gap-6` inside and `gap-8` between). THIS COMPONENT OWNS NO STATE: every edit is emitted as a
 * whole replacement draft, so `menuPatch` computes one clean diff.
 *
 * The preview sits after the fields it previews and before the switch, so the last thing read
 * before turning a menu on is what the customer will see.
 *
 * Every two-language field is ONE `BilingualBox` (P-7.1), and on a Both account the fields
 * written in one language only are listed with "Translate them for me" (P-2.10). A one-language
 * account sees only its language.
 */
import { computed } from 'vue';

import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

import BilingualBox from './task/BilingualBox.vue';
import MenuOptionList from './MenuOptionList.vue';
import MenuPreview from './MenuPreview.vue';
import MissingLanguageNotice from './task/MissingLanguageNotice.vue';
import WasloExplainer from './WasloExplainer.vue';
import WasloInput from './WasloInput.vue';
import WasloTextArea from './WasloTextArea.vue';
import { useWasloI18n } from '../i18n.js';
import { LIMITS } from '../helpers/menu-draft.js';
import { fieldFor, menuMissingWords } from '../helpers/missing-language.js';

const props = defineProps({
  /** The draft, in the shape `helpers/menu-draft.js` builds. */
  modelValue: { type: Object, required: true },
  /** From `validateDraft`, or the BFF's `error.fields`. */
  errors: { type: Object, default: () => ({}) },
  /** `[{ id, title, enabled }]`, or null when that read failed. */
  tasks: { type: Array, default: null },
  /** The account's "Your customers write in" (P-7.1). */
  languages: { type: String, default: 'both' },
});

const emit = defineEmits(['update:modelValue']);

const { t, locale } = useWasloI18n();

const patch = changes =>
  emit('update:modelValue', { ...props.modelValue, ...changes });

const previewLang = computed(() =>
  String(locale.value).startsWith('ar') ? 'ar' : 'en'
);

/** Every field written in one language only, on a Both account (P-7.1, P-2.10). */
const missingWords = computed(() =>
  menuMissingWords(props.modelValue, props.languages)
);

/** One "Use this" from the warning, written to the field (or the option) it names. */
const applyWords = ({ row, text }) => {
  const field = fieldFor(row.target, row.to);
  if (row.target.optionKey === null) {
    patch({ [field]: text });
    return;
  }
  patch({
    options: props.modelValue.options.map(option =>
      option.key === row.target.optionKey
        ? { ...option, [field]: text }
        : option
    ),
  });
};
</script>

<template>
  <div class="flex flex-col gap-8 pb-8" data-waslo-menu-form>
    <WasloExplainer
      icon="i-lucide-list"
      :note="t('WASLO.MENUS.EDITOR.SPOTLIGHT')"
    />

    <!-- 1 · WHAT IT IS, AND WHEN IT SHOWS. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t('WASLO.MENUS.EDITOR.ABOUT.HEADING')"
        :description="t('WASLO.MENUS.EDITOR.ABOUT.DESCRIPTION')"
      />
      <WasloInput
        :model-value="modelValue.title"
        :label="t('WASLO.MENUS.EDITOR.NAME.LABEL')"
        :placeholder="t('WASLO.MENUS.EDITOR.NAME.PLACEHOLDER')"
        :hint="t('WASLO.MENUS.EDITOR.NAME.HINT')"
        :message="errors.title || ''"
        :message-type="errors.title ? 'error' : 'info'"
        data-waslo-menu-title-field
        @update:model-value="value => patch({ title: value })"
      />
      <WasloTextArea
        :model-value="modelValue.whenToShow"
        :label="t('WASLO.MENUS.EDITOR.WHEN.LABEL')"
        :placeholder="t('WASLO.MENUS.EDITOR.WHEN.PLACEHOLDER')"
        :hint="t('WASLO.MENUS.EDITOR.WHEN.HINT')"
        :message="errors.whenToShow || ''"
        message-type="error"
        :max-length="LIMITS.WHEN"
        min-height="3.5rem"
        @update:model-value="value => patch({ whenToShow: value })"
      />
    </div>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 2 · THE OPTIONS. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t('WASLO.MENUS.EDITOR.OPTIONS.HEADING')"
        :description="t('WASLO.MENUS.EDITOR.OPTIONS.DESCRIPTION')"
      />
      <MenuOptionList
        :model-value="modelValue.options"
        :tasks="tasks"
        :message="errors.options || ''"
        :languages="languages"
        @update:model-value="value => patch({ options: value })"
      />
    </div>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 3 · THE WORDS AROUND IT. Used only when the reply is too long to carry the menu. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t('WASLO.MENUS.EDITOR.MESSAGE.HEADING')"
        :description="t('WASLO.MENUS.EDITOR.MESSAGE.DESCRIPTION')"
      />
      <BilingualBox
        :en="modelValue.bodyEn"
        :ar="modelValue.bodyAr"
        :label="t('WASLO.MENUS.EDITOR.MESSAGE.BODY')"
        :message="errors.bodyEn || errors.bodyAr || ''"
        :max-length="LIMITS.BODY"
        multiline
        min-height="3.5rem"
        kind="menu_body"
        :languages="languages"
        data-waslo-menu-body
        @update:en="value => patch({ bodyEn: value })"
        @update:ar="value => patch({ bodyAr: value })"
        @update:pair="pair => patch({ bodyEn: pair.en, bodyAr: pair.ar })"
      />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.MENUS.EDITOR.MESSAGE.BODY_HINT') }}
      </p>
      <BilingualBox
        :en="modelValue.buttonEn"
        :ar="modelValue.buttonAr"
        :label="t('WASLO.MENUS.EDITOR.MESSAGE.BUTTON')"
        :message="errors.buttonEn || errors.buttonAr || ''"
        :max-length="LIMITS.BUTTON"
        counter
        kind="menu_button"
        :languages="languages"
        data-waslo-menu-button
        @update:en="value => patch({ buttonEn: value })"
        @update:ar="value => patch({ buttonAr: value })"
        @update:pair="pair => patch({ buttonEn: pair.en, buttonAr: pair.ar })"
      />
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('WASLO.MENUS.EDITOR.MESSAGE.BUTTON_HINT') }}
      </p>
      <MissingLanguageNotice :rows="missingWords" @apply="applyWords" />
    </div>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 4 · WHAT THE CUSTOMER WILL SEE. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t('WASLO.MENUS.EDITOR.PREVIEW.HEADING')"
        :description="t('WASLO.MENUS.EDITOR.PREVIEW.DESCRIPTION')"
      />
      <MenuPreview :draft="modelValue" :initial-lang="previewLang" />
    </div>

    <span class="w-full h-px mt-2 bg-n-weak" />

    <!-- 5 · WHETHER IT RUNS. -->
    <div class="flex flex-col gap-6">
      <SettingsHeader
        :heading="t('WASLO.MENUS.EDITOR.STATE.HEADING')"
        :description="t('WASLO.MENUS.EDITOR.STATE.DESCRIPTION')"
      />
      <div
        class="flex items-start justify-between gap-6 p-4 outline outline-1 rounded-xl outline-n-weak bg-n-solid-1"
      >
        <div class="flex flex-col gap-1">
          <h3 class="mb-0 text-sm font-medium text-n-slate-12">
            {{ t('WASLO.MENUS.EDITOR.STATE.LABEL') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{
              modelValue.enabled
                ? t('WASLO.MENUS.EDITOR.STATE.ON')
                : t('WASLO.MENUS.EDITOR.STATE.OFF')
            }}
          </p>
        </div>
        <Switch
          :model-value="modelValue.enabled"
          :aria-label="t('WASLO.MENUS.EDITOR.STATE.LABEL')"
          class="mt-1 shrink-0"
          data-waslo-menu-enabled-switch
          @update:model-value="value => patch({ enabled: value })"
        />
      </div>
    </div>

    <!-- Removal is the page's, not the form's. -->
    <slot name="danger" />
  </div>
</template>
